import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderOpen, Trash2, LineChart, Clock, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { listProjects, createProject, deleteProject } from '../lib/api';

export default function Dashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', symbol: '', exchange: '', timeframe: '' });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listProjects();
      setProjects(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newProject.name.trim()) return;
    try {
      const p = await createProject(newProject);
      // Close dialog first, then navigate after a tick
      setCreateOpen(false);
      setNewProject({ name: '', symbol: '', exchange: '', timeframe: '' });
      // Small delay to let dialog unmount before navigation
      setTimeout(() => {
        navigate(`/analysis/${p.id}`);
      }, 100);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Delete this project and all its analyses?')) return;
    try {
      await deleteProject(id);
      load();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="max-w-6xl animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">Projects</h1>
          <p className="text-sm text-[#a1a1aa] mt-1">Manage your Elliott Wave analysis projects</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-project-btn" className="bg-[#f5b400] text-black hover:bg-[#d9a000] rounded-sm font-medium active:scale-95 transition-transform">
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#14161a] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Create Project</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa] mb-1.5 block">Project Name *</label>
                <Input
                  data-testid="project-name-input"
                  value={newProject.name}
                  onChange={e => setNewProject({ ...newProject, name: e.target.value })}
                  placeholder="e.g., BTC/USD Elliott Analysis"
                  className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa] mb-1.5 block">Symbol</label>
                  <Input
                    data-testid="project-symbol-input"
                    value={newProject.symbol}
                    onChange={e => setNewProject({ ...newProject, symbol: e.target.value })}
                    placeholder="BTCUSD"
                    className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa] mb-1.5 block">Exchange</label>
                  <Input
                    value={newProject.exchange}
                    onChange={e => setNewProject({ ...newProject, exchange: e.target.value })}
                    placeholder="Binance"
                    className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa] mb-1.5 block">Timeframe</label>
                  <Input
                    value={newProject.timeframe}
                    onChange={e => setNewProject({ ...newProject, timeframe: e.target.value })}
                    placeholder="4H"
                    className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm font-mono text-sm"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-white/10 text-white hover:bg-white/5 rounded-sm">Cancel</Button>
              <Button data-testid="confirm-create-btn" onClick={handleCreate} className="bg-[#f5b400] text-black hover:bg-[#d9a000] rounded-sm">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Project Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="wk-panel h-40 animate-pulse" />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <Card className="bg-[#14161a] border-white/5">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FolderOpen className="w-12 h-12 text-[#a1a1aa]/30 mb-4" />
            <p className="text-[#a1a1aa] text-sm mb-4">No projects yet. Create one to start analyzing.</p>
            <Button onClick={() => setCreateOpen(true)} className="bg-[#f5b400] text-black hover:bg-[#d9a000] rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> Create First Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
          {projects.map((p, idx) => (
            <div
              key={p.id}
              data-testid={`project-card-${idx}`}
              onClick={() => navigate(`/analysis/${p.id}`)}
              className={`wk-panel p-4 cursor-pointer hover:border-[#f5b400]/30 transition-all duration-200 animate-fade-in stagger-${Math.min(idx + 1, 5)} group`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-white truncate group-hover:text-[#f5b400] transition-colors">{p.name}</h3>
                  {p.symbol && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-[#f5b400]/10 text-[#f5b400] border border-[#f5b400]/20 font-mono">
                      {p.symbol}
                    </span>
                  )}
                </div>
                <button
                  onClick={(e) => handleDelete(e, p.id)}
                  data-testid={`delete-project-${idx}`}
                  className="p-1.5 rounded-sm hover:bg-red-500/10 text-[#a1a1aa] hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="flex items-center gap-4 text-xs text-[#a1a1aa]">
                {p.exchange && <span>{p.exchange}</span>}
                {p.timeframe && <span className="font-mono">{p.timeframe}</span>}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
                <div className="flex items-center gap-1.5 text-xs text-[#a1a1aa]">
                  <LineChart className="w-3.5 h-3.5" />
                  <span className="font-mono">{p.analysis_count || 0}</span>
                  <span>analyses</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-[#a1a1aa]">
                  <Clock className="w-3 h-3" />
                  <span>{new Date(p.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[#a1a1aa]/0 group-hover:text-[#f5b400] absolute right-3 top-1/2 -translate-y-1/2 transition-all" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
